import { ITourStep } from "training/types";
import { ITrainingProgress } from "types/Training";

/**
 * CLE-3015 — where a (re)started tour begins, from the saved progress. Pure, so the resume rules are
 * unit-tested without driver.js.
 *
 * - No progress, or progress saved under another curriculum version → fresh progress, first step.
 * - `currentStepId` set → resume there. If that step is not in this run (its condition is now false, or
 *   it was removed), resume at the next step of this run after it in curriculum order.
 *   A step on the create page restarts at the first create-page step: a reload or a fresh visit
 *   loses the half-filled form, so resuming at "Create and Send" would point at an empty page.
 *   A step with `resumeAt` restarts there (PR 3): the patient page and its checkout cannot be opened by
 *   URL, so a saved patient-page step restarts at the Collect Payment click (4.1) that opens it; a step
 *   inside the Reports tab restarts at the tab click (6.4).
 * - `currentStepId` empty (finished, or never moved) → the first step neither completed nor skipped;
 *   if there is none, a fresh run from step 1 (the quiz record is kept).
 */
export interface IResolvedStart {
  progress: ITrainingProgress;
  startStepId: string;
  resumed: boolean;
}

export function freshProgress(
  version: number,
  firstStepId: string,
  now: Date
): ITrainingProgress {
  return {
    version,
    startedAt: now.toISOString(),
    currentStepId: firstStepId,
    completed: [],
    skipped: [],
  };
}

/** The first step of `visible` at or after `stepId` in curriculum order, or undefined. */
export function nextVisibleFrom(
  curriculum: ITourStep[],
  visible: ITourStep[],
  stepId: string
): ITourStep | undefined {
  const direct = visible.find((step) => step.id === stepId);
  if (direct) return direct;
  const visibleIds = new Set(visible.map((step) => step.id));
  const position = curriculum.findIndex((step) => step.id === stepId);
  if (position === -1) return undefined;
  return curriculum
    .slice(position + 1)
    .map((step) => visible.find((v) => v.id === step.id))
    .find((step): step is ITourStep => !!step && visibleIds.has(step.id));
}

/**
 * Where a step's `resumeAt` points in this run: that curriculum step (or the next visible one after it),
 * or the menu-opening click the engine put in front of it (`<id>-open`), so a resume never lands on an
 * element inside a closed menu.
 */
export function resumeTarget(
  curriculum: ITourStep[],
  visible: ITourStep[],
  resumeAt: string
): ITourStep | undefined {
  const target = nextVisibleFrom(curriculum, visible, resumeAt);
  if (!target) return undefined;
  const index = visible.indexOf(target);
  const before = visible[index - 1];
  return before?.id === `${target.id}-open` ? before : target;
}

export function resolveStart({
  saved,
  serverVersion,
  curriculum,
  visible,
  now = new Date(),
}: {
  saved: ITrainingProgress | null | undefined;
  serverVersion: number;
  curriculum: ITourStep[];
  visible: ITourStep[];
  now?: Date;
}): IResolvedStart {
  const first = visible[0];
  const fresh = (): IResolvedStart => ({
    progress: freshProgress(serverVersion, first.id, now),
    startStepId: first.id,
    resumed: false,
  });

  if (!saved || saved.version !== serverVersion) return fresh();

  const progress: ITrainingProgress = {
    ...saved,
    completed: [...(saved.completed ?? [])],
    skipped: [...(saved.skipped ?? [])],
  };

  if (saved.currentStepId) {
    let step = nextVisibleFrom(curriculum, visible, saved.currentStepId);
    if (step?.route === "create") {
      step = visible.find((s) => s.route === "create") ?? step;
    }
    if (step?.resumeAt) {
      step = resumeTarget(curriculum, visible, step.resumeAt) ?? step;
    }
    if (step) {
      return {
        progress: { ...progress, currentStepId: step.id },
        startStepId: step.id,
        resumed: true,
      };
    }
  }

  const done = new Set([
    ...progress.completed,
    ...progress.skipped.map((skip) => skip.stepId),
  ]);
  const open = visible.find((step) => !done.has(step.id));
  if (open) {
    return {
      progress: { ...progress, currentStepId: open.id },
      startStepId: open.id,
      resumed: true,
    };
  }

  const restart = fresh();
  return saved.quiz
    ? { ...restart, progress: { ...restart.progress, quiz: saved.quiz } }
    : restart;
}
